import { css } from '@emotion/react';
import { atoms, colors } from '@zapier/marketing-ui';
import { Icon } from '@zapier/zinnia-icons';
import { ReactNode, useEffect, useId, useRef } from 'react';

type ModalProps = {
  children: ReactNode;
  onClose?: () => void;
  maxWidth?: string;
  title?: string;
};

type ModalBodyProps = {
  children: ReactNode;
};

type ModalFooterProps = {
  children: ReactNode;
};

const styles = {
  root: css`
    border: none;
    border-radius: 8px;
    padding: 32px;
    max-width: 90vw;
    max-height: 90vh;
    box-shadow: 0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1);
    position: relative;

    &::backdrop {
      background-color: rgba(0, 0, 0, 0.5);
    }
  `,

  title: css`
    margin-bottom: 24px;
    padding-right: 32px;
  `,

  close: css`
    position: absolute;
    top: 16px;
    right: 16px;
    background: none;
    border: none;
    line-height: 1;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    color: ${colors.neutral700};
    transition: color 150ms;

    &:hover {
      color: ${colors.neutral900};
    }

    /* Keep: the dialog focuses this button first, so it needs a visible ring (SC 2.4.7). */
    &:focus-visible {
      outline: 2px solid ${colors.neutral900};
      outline-offset: 2px;
    }
  `,

  body: css`
    display: grid;
    gap: 20px;
  `,

  footer: css`
    display: flex;
    justify-content: flex-end;
    gap: 12px;
    margin-top: 40px;
  `,
};

export function Modal({
  children,
  onClose,
  maxWidth = '576px',
  title,
}: ModalProps) {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const titleId = useId();

  useEffect(() => {
    dialogRef.current?.showModal();
  }, []);

  const handleClose = () => {
    dialogRef.current?.close();
  };

  return (
    <dialog
      ref={dialogRef}
      css={styles.root}
      style={{ width: maxWidth }}
      onClose={onClose}
      aria-labelledby={title ? titleId : undefined}
    >
      {onClose && (
        <button
          type="button"
          css={styles.close}
          onClick={handleClose}
          aria-label="Close"
        >
          <Icon name="formX" size={24} />
        </button>
      )}
      {title && (
        <h2 id={titleId} className={atoms.headingXS} css={styles.title}>
          {title}
        </h2>
      )}
      {children}
    </dialog>
  );
}

export function ModalBody({ children }: ModalBodyProps) {
  return (
    <div className={atoms.body} css={styles.body}>
      {children}
    </div>
  );
}

export function ModalFooter({ children }: ModalFooterProps) {
  return <div css={styles.footer}>{children}</div>;
}
